import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useUserContext } from '../../Contexts/UserConfig'
import { useMessagePopupContext } from '../../garvan-react/Components/ModalFeedback/MessagePopup'
import { useNavigate } from 'react-router-dom'
import { AxiosResponse } from 'axios'
import SampleRunService from '../PHP/APIs/SampleRunService'
import {
  tsGetSampleRun,
  tsGetSampleRunsInterface,
} from '../../Typescript/API_Response'
import { useSendPrintVessel } from './PrintingHooks'
import { useMachineContext } from '../../Contexts/CurrentMachine'
import { useHoldingContext } from '../../Contexts/CurrentHolding'

export interface NewSampleRunPayload {
  holding_id: number
  owner_id?: string | number
  run_type: string
  destination: number
  vessel_type: string
  collection_id?: string
}
export function useNewSampleRun() {
  const queryClient = useQueryClient()
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  const navigate = useNavigate()
  return useMutation<any, AxiosResponse, NewSampleRunPayload, () => void>(
    ['SampleRunService', 'newSampleRun'],
    (newRun) => SampleRunService.newSampleRun(whoAmI!, newRun),
    {
      onSuccess: async (data) => {
        if (data?.SampleRunID) {
          navigate({
            pathname: '/stuart/sample/run/' + data.SampleRunID,
          })
          await queryClient.invalidateQueries({
            queryKey: ['SampleRunService'],
          })
        } else {
          setMessage({
            subject: 'Unable to save result comment',
            message: data.message,
            severity: 'warning',
          })
        }
      },
      onError: (err) => setError(err),
    },
  )
}

export function useGetSampleRun(sample_run_id: string | undefined) {
  const { whoAmI } = useUserContext()
  const { setError, setMessage } = useMessagePopupContext()
  return useQuery<tsGetSampleRun, AxiosResponse>(
    ['SampleRunService', 'getSampleRun', sample_run_id],
    () => SampleRunService.getSampleRun(whoAmI!, sample_run_id!),
    {
      onSuccess: (data) => {
        if (!data?.runInfo) {
          setMessage({
            subject: 'Error getting sample run',
            message: (data as any)?.message,
            severity: 'warning',
          })
        }
      },
      onError: (err) => setError(err),
      enabled: !!whoAmI && !!sample_run_id,
    },
  )
}

export function useGetSampleRunsForHolding() {
  const { whoAmI } = useUserContext()
  const { currentHolding } = useHoldingContext()
  const { setError } = useMessagePopupContext()
  return useQuery<tsGetSampleRunsInterface, AxiosResponse>(
    ['SampleRunService', 'getSampleRunsForHolding', currentHolding.value],
    () =>
      SampleRunService.getSampleRunsForHolding(whoAmI!, currentHolding.value),
    {
      onError: (err) => setError(err),
      enabled: !!whoAmI && !!currentHolding.value,
    },
  )
}

export function useEditSampleRun(
  sampleRun: tsGetSampleRun | undefined,
  setOpen: any,
) {
  const { whoAmI } = useUserContext()
  const queryClient = useQueryClient()
  const { cardPrinter } = useMachineContext()
  const { setError, setMessage } = useMessagePopupContext()
  const { mutate: submitPrint } = useSendPrintVessel(cardPrinter)
  return useMutation<any, AxiosResponse, string, () => void>(
    ['SampleRunService', 'newSampleRun'],
    async (newStatus) => {
      const update = await SampleRunService.editSampleRun(whoAmI!, {
        EmployeeID: sampleRun!.runInfo.EmployeeID,
        // RunDate: null,
        RunType: sampleRun!.runInfo.RunType,
        DestinationFacilityID: sampleRun!.runInfo.DestinationFacilityID,
        Status: newStatus,
        SampleRunID: sampleRun!.runInfo.SampleRunID,
        OriginHoldingID: sampleRun!.runInfo.OriginHoldingID,
      })
      return { ...update, SampleRunID: sampleRun!.runInfo.SampleRunID }
    },
    {
      onSuccess: async (data, newStatus) => {
        if (data?.success) {
          if (newStatus !== 'DES') {
            submitPrint(data.SampleRunID)
          }
          await queryClient.invalidateQueries({
            queryKey: ['SampleRunService'],
          })
          await setOpen(false)
        } else {
          setMessage({
            subject: 'Error saving sample run changes',
            message: (data as any)?.message,
            severity: 'warning',
          })
        }
      },
      onError: (err) => setError(err),
    },
  )
}
